import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import LandingHeader from "../components/LandingHeader";
import SiteFooter from "../components/SiteFooter";
import RelatedLinks from "../components/RelatedLinks";
import { WHATSAPP_LINK } from "../utils/constants";

const FAQS = [
  {
    q: "How fast will I hear back?",
    a: "We read every message and reply as soon as we can. If your question is about bookkeeping, WhatsApp is usually the fastest route, because it goes straight to the product. For anything else, email or phone works best. Please include a short description of what you need so we can point you to the right place.",
  },
  {
    q: "I am a subscriber and need help with my books. What should I do?",
    a: "Start in the product itself: send your receipt or question through WhatsApp bookkeeping, or open the Scan Center and scan from there. If you still need a person, email feed@moneyfeed.io or call +1 (832) 688-6863 and tell us you are a subscriber, so we know which account you mean. Please do not send passwords by email.",
  },
  {
    q: "Can I talk to a person, not just the WhatsApp product?",
    a: "Yes. The WhatsApp number on this page is the MoneyFeed product, which helps you record expenses and scan receipts. For a person, use email at feed@moneyfeed.io or call +1 (832) 688-6863. Those are the human business contact details published on this site.",
  },
  {
    q: "I am an accountant or a potential partner. How do I get in touch?",
    a: "Start on the For Accountants page, then email feed@moneyfeed.io or call +1 (832) 688-6863 and mention that your enquiry is about a partnership. Tell us a little about your practice and what you would like to explore, and we will take it from there.",
  },
  {
    q: "Does MoneyFeed file or prepare my taxes?",
    a: "No. MoneyFeed is bookkeeping software, not a tax preparer. It helps you record, categorize and organize receipts and expenses so your records are ready to hand to your CPA or tax professional. Nothing on this page changes that, and this page is not tax advice.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Contact() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D9488]/10 via-white to-white">
      <LandingHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Seo
          title="Contact MoneyFeed | Email, Phone and WhatsApp Bookkeeping"
          description="Contact MoneyFeed by email at feed@moneyfeed.io, by phone at +1 (832) 688-6863, or start bookkeeping on WhatsApp. Find our Austin mailing address and answers for subscribers and partners."
          path="/contact"
          jsonLd={jsonLd}
        />
        <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900">Contact MoneyFeed</h1>
        <p className="mt-4 text-lg text-gray-700 leading-relaxed">
          The fastest way to reach us depends on what you need. For bookkeeping, start in the product on WhatsApp or in the Scan Center. For a person, use the email address or phone number below. Both are published in the footer of every page on this site.
        </p>
        <section className="mt-10 grid gap-6 sm:grid-cols-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900">Email</h2>
            <p className="mt-2 text-gray-700 leading-relaxed">For general questions, subscriber help and partnership enquiries.</p>
            <a href="mailto:feed@moneyfeed.io" className="mt-3 inline-block font-semibold text-[#0D9488]">feed@moneyfeed.io</a>
            <p className="mt-2 text-sm text-gray-600">Please do not send passwords by email. A short description of your question helps us reply faster.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900">Phone</h2>
            <p className="mt-2 text-gray-700 leading-relaxed">Call us for a person-to-person conversation about MoneyFeed.</p>
            <a href="tel:+18326886863" className="mt-3 inline-block font-semibold text-[#0D9488]">+1 (832) 688-6863</a>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900">Mailing address</h2>
            <p className="mt-2 text-gray-700 leading-relaxed">5900 Balcones Drive 7274<br />Austin, TX 78731<br />United States</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900">WhatsApp bookkeeping (product)</h2>
            <p className="mt-2 text-gray-700 leading-relaxed">This is the MoneyFeed product, not the human business contact above. Send a receipt photo, a text or a voice note and the product helps you record it.</p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="mt-3 inline-block font-semibold text-[#0D9488]">Start on WhatsApp: +1 737-316-3545</a>
          </div>
        </section>
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-gray-900">For accountants and partners</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">If you are an accountant, a bookkeeper or a firm interested in working with MoneyFeed, start on the For Accountants page to see how the product works for client books. Then email feed@moneyfeed.io or call +1 (832) 688-6863 and mention that your enquiry is about a partnership.</p>
          <Link to="/for-accountants" className="mt-4 inline-block font-semibold text-[#0D9488]">Visit For Accountants</Link>
        </section>
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-gray-900">Frequently asked questions</h2>
          <div className="mt-4 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-bold text-gray-900">{f.q}</h3>
                <p className="mt-2 text-gray-700 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="mt-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Ready to get your books in order?</h2>
          <p className="mt-2 text-gray-700">Scan a receipt, or start bookkeeping on WhatsApp, and keep your records ready for your CPA.</p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/scan-center" className="inline-block bg-[#0D9488] text-white px-6 py-3 rounded-full font-semibold">Explore Scan Center</Link>
            <Link to="/auth" className="inline-block border border-gray-300 px-6 py-3 rounded-full font-semibold text-gray-900">Start free</Link>
            <Link to="/pricing" className="inline-block font-semibold text-[#0D9488]">See pricing</Link>
          </div>
        </section>
        <RelatedLinks links={[
          { title: "Scan Center", href: "/scan-center", description: "Scan receipts and keep your books in one place." },
          { title: "WhatsApp Bookkeeping", href: "/whatsapp-bookkeeping", description: "Bookkeeping on WhatsApp, by text or voice." },
          { title: "For Accountants", href: "/for-accountants", description: "MoneyFeed for accountants and their clients." },
          { title: "Pricing", href: "/pricing", description: "Plans, scan quotas and what each plan includes." },
        ]} />
      </main>
      <SiteFooter />
    </div>
  );
}
